/**
 * Shared presentational layer for plan cards.
 * Used by PlanComparisonCard (pricing/register) and PlanAdminCard (billing admin).
 *
 * Renders: voice status, resource limits, benefits.
 * Does NOT render: card chrome, header/price, CTA buttons, status badges;
 * `PlanCreditsLine` is the one header piece both cards share.
 *
 * What those rows contain lives in `plan-features.ts` — the mobile registration
 * list renders the same data in a different shape.
 */

import { type ReactNode } from 'react'
import { Check, Mic, MicOff, Clock, LineChart, type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  formatPlanLimitRow,
  getInsightsTrialText,
  getPlanBenefits,
  getPlanCreditsLifetimeText,
  getPlanCreditsText,
  getPlanLimitRows,
  getPlanVoiceLabel,
  type PlanCreditFields,
  type PlanCreditLifetimeFields,
  type PlanVoiceStatus,
} from './plan-features'

export function PlanCreditsLine({ plan }: { plan: PlanCreditFields & PlanCreditLifetimeFields }) {
  const text = getPlanCreditsText(plan)
  if (!text) return null
  const lifetime = getPlanCreditsLifetimeText(plan)
  return (
    <>
      <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{text}</p>
      {lifetime && <p className="text-xs text-muted-foreground">{lifetime}</p>}
    </>
  )
}

interface PlanCardBodyProps {
  /** Plan fields needed for presentation */
  plan: {
    voice_included: boolean
    voice_trial_enabled: boolean
    voice_trial_duration_hours: number | null
    /**
     * Shopify Insights trial length in hours. Positive = time-limited trial
     * (shows a badge); null/absent = included without limit (no badge). Optional
     * because the public comparison card's plan projection omits it.
     */
    shopify_insights_trial_duration_hours?: number | null
    voice_agents_limit: number
    campaigns_enabled?: boolean
    campaigns_limit?: number | null
    pbx_numbers_limit: number | null
    knowledge_bases_limit: number | null
    knowledge_bases_size_mb_limit: number | null
    users_limit: number | null
    crm_integrations_limit: number | null
    shopify_stores_limit: number | null
    marketing_channels_limit?: number | null
    benefits: string[] | unknown
  }
  /** Max benefits to show before "+N more" (default: all) */
  maxBenefits?: number
}

export function PlanCardBody({
  plan,
  maxBenefits,
}: PlanCardBodyProps) {
  const voice = getPlanVoiceLabel(plan)
  const voiceAccent = voiceAccentFor(voice.status)
  const VoiceIcon = voiceAccent.icon

  // Rendered inline on the "Shopify stores" row (Shopify-only), so it reads as a
  // Shopify-scoped perk with a tooltip instead of an ambiguous standalone line.
  const insightsTrialText = getInsightsTrialText(plan.shopify_insights_trial_duration_hours)

  const insightsTrialHint: ReactNode = insightsTrialText ? (
    <TooltipProvider>
      <Tooltip>
        {/* No `cursor-help` here: `TooltipTrigger` reads that class as "this is a
            question-mark hint" and wraps the child in a fixed 32x32 tap target,
            which a pill this wide overflows on both sides. */}
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            <LineChart className="h-3 w-3" />
            Insights trial: {insightsTrialText}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-xs">
          <span>Channel analytics, Cross-Sell, Economics and Bundle Rules are free for</span>{' '}
          {insightsTrialText} <span>on this plan, then need an upgrade. Revenue and order
          totals, Orders and Catalog stay free.</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null

  const limits = getPlanLimitRows(plan)
  const hasInsightsTrialHint = Boolean(insightsTrialHint)

  const benefits = getPlanBenefits(plan.benefits)
  const shownBenefits = maxBenefits ? benefits.slice(0, maxBenefits) : benefits
  const hiddenCount = maxBenefits && benefits.length > maxBenefits ? benefits.length - maxBenefits : 0

  return (
    <>
      {/* Voice status */}
      <div className={`flex items-center gap-1.5 text-sm font-medium ${voiceAccent.className}`}>
        <VoiceIcon className="h-4 w-4" />
        {voice.text}
      </div>

      {/* Resource limits */}
      <ul className="space-y-1.5 text-sm text-muted-foreground flex-1">
        {limits.map((limit) => (
          <li key={limit.label} className="flex items-start gap-2">
            <Check className="mt-[3px] h-3.5 w-3.5 text-green-500 shrink-0" />
            <span className="min-w-0">
              {formatPlanLimitRow(limit)}
              {/* Its own line under the label: sharing one leaves the label
                  and the pill fighting over a card barely wider than either of
                  them, and one of the two always loses. Indented and held tight
                  to the row above so it reads as belonging to it — the pill's
                  own padding would otherwise start it left of the label. */}
              {limit.shopifyStores && hasInsightsTrialHint ? (
                <span className="mt-0.5 block pl-4">{insightsTrialHint}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>

      {/* Benefits */}
      {shownBenefits.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground border-t pt-3">
          {shownBenefits.map((benefit, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <Check className="h-3 w-3 text-blue-500 shrink-0 mt-0.5" />
              {benefit}
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="text-muted-foreground/60"><span>+</span>{hiddenCount} <span>more</span></li>
          )}
        </ul>
      )}
    </>
  )
}

function voiceAccentFor(status: PlanVoiceStatus): { icon: LucideIcon; className: string } {
  switch (status) {
    case 'included':
      return { icon: Mic, className: 'text-green-600' }
    case 'trial':
      return { icon: Clock, className: 'text-amber-600' }
    case 'none':
      return { icon: MicOff, className: 'text-muted-foreground' }
  }
}
